import type { AccessNodePublic, AccessPasswordHandling, ApplicationPublic } from '@accounts/shared';
import { ACCESS_PASSWORD_HANDLINGS } from '@accounts/shared';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import { relativeTime } from '../../../../lib/format';
import { Modal, ModalForm } from '../../../../ui/Modal';
import { Callout } from '../../../../ui/Primitives';
import { fieldLabelStyle, hintStyle, modalButtonStyle, textFieldStyle } from '../../../../ui/styles';
import { itemLabel, passwordHandlingLabel } from '../accessFormat';
import { type RowDraft, RowFields } from '../catalog/RowFields';

/** What the two dialogs need to know about one Other request. */
export interface OtherRequestView {
  id: string;
  personName: string;
  /** "What they need" — the request's own words. */
  what: string;
  /** "Where it belongs", when they said: the node they picked, or null. */
  whereNodeId: string | null;
  whereLabel: string | null;
  /** "Why" — may be empty. */
  why: string;
  createdAt: string;
}

/** The status a new outside-system record starts in when an Other request is approved. */
export type OtherGrantStatus = 'needs_access' | 'active';

export interface OtherApproveDraft {
  row: RowDraft;
  /** The record for an outside system; null for a suite app (the grant needs nothing). */
  record: {
    login: string | null;
    passwordHandling: AccessPasswordHandling;
    status: OtherGrantStatus;
  } | null;
}

function firstName(name: string): string {
  return name.split(' ')[0] ?? name;
}

/**
 * APPROVE AN OTHER REQUEST (spec row 13; FINAL demo change 3): Add catalog row, pre-filled
 * from the request, plus who gets it. Saving adds the row AND gives it to the person in one
 * step — the server does both in one transaction, so a row is never left added but not
 * given. A suite app becomes a grant (no password step); an outside system becomes an access
 * record (login optional, Managed or Given raw, Needs access or Active). Approve stays
 * disabled for an outside system until the password mode is picked (spec row 16).
 */
export function OtherApproveDialog({
  request,
  nodes,
  applications,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  request: OtherRequestView;
  nodes: readonly AccessNodePublic[];
  applications: readonly ApplicationPublic[];
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (draft: OtherApproveDraft) => void;
}) {
  const [row, setRow] = useState<RowDraft>({
    name: request.what,
    nodeId: request.whereNodeId,
    applicationId: null,
    requestable: true,
    hrCanGrant: false,
  });
  const [login, setLogin] = useState('');
  const [mode, setMode] = useState<AccessPasswordHandling | null>(null);
  const [status, setStatus] = useState<OtherGrantStatus>('needs_access');
  const [tried, setTried] = useState(false);

  const first = firstName(request.personName);
  const app = applications.find((candidate) => candidate.id === row.applicationId) ?? null;
  const place = nodes.find((node) => node.id === row.nodeId);
  const nameMissing = row.name.trim() === '';
  const modeMissing = app === null && mode === null;
  const preview = itemLabel(place?.path ?? [], nameMissing ? STR.access.catalog.namePending : row.name.trim());

  function submit() {
    setTried(true);
    if (nameMissing || modeMissing) return;
    onConfirm({
      row: { ...row, name: row.name.trim() },
      record:
        app !== null || mode === null
          ? null
          : { login: login.trim() === '' ? null : login.trim(), passwordHandling: mode, status },
    });
  }

  return (
    <Modal
      title={STR.access.catalog.addTitle}
      lead={STR.otherRequests.approveLead}
      width="min(640px,100%)"
      dismissable={!pending}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="ghost"
            style={modalButtonStyle}
            disabled={pending}
            onClick={onCancel}
          >
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={pending || modeMissing}
            onClick={submit}
          >
            {pending ? STR.otherRequests.approving : STR.otherRequests.addAndGrant(first)}
          </button>
        </>
      }
    >
      <ModalForm>
        <Callout tone="neutral">
          {STR.otherRequests.fromRequest(
            request.personName,
            relativeTime(request.createdAt),
            request.what,
            request.why,
            request.whereLabel,
            first,
          )}
        </Callout>
        <RowFields
          draft={row}
          onChange={(next) => {
            setRow(next);
          }}
          nodes={nodes}
          applications={applications}
          disabled={pending}
          idPrefix="other-row"
        />
        <div className="tx-subtle" style={{ fontSize: 12 }}>
          {STR.access.catalog.willBeCalled(preview)}
        </div>
        {tried && nameMissing ? (
          <span className="tx-danger-strong" role="alert" style={hintStyle}>
            {STR.access.catalog.nameRequired}
          </span>
        ) : null}

        <div
          style={{
            borderTop: '1px solid var(--border)',
            paddingTop: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 650 }}>{STR.otherRequests.giveTo(request.personName)}</div>
          {app !== null ? (
            <Callout tone="neutral">{STR.otherRequests.suiteGrant(first, app.name)}</Callout>
          ) : (
            <>
              <label>
                <span className="tx-muted" style={fieldLabelStyle}>
                  {STR.otherRequests.loginLabel}
                </span>
                <input
                  className="field"
                  value={login}
                  disabled={pending}
                  placeholder={STR.otherRequests.loginPlaceholder}
                  onChange={(event) => {
                    setLogin(event.target.value);
                  }}
                  style={textFieldStyle}
                />
              </label>
              <div>
                <span className="tx-muted" style={fieldLabelStyle}>
                  {STR.access.record.passwordLabel}
                </span>
                <span className="tgl" role="group" aria-label={STR.access.record.passwordLabel}>
                  {ACCESS_PASSWORD_HANDLINGS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="seg"
                      aria-pressed={mode === value}
                      disabled={pending}
                      onClick={() => {
                        setMode(value);
                      }}
                      style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 650 }}
                    >
                      {passwordHandlingLabel(value)}
                    </button>
                  ))}
                </span>
                <span
                  className={tried && modeMissing ? 'tx-danger-strong' : 'tx-subtle'}
                  style={hintStyle}
                  {...(tried && modeMissing ? { role: 'alert' } : {})}
                >
                  {mode === null
                    ? STR.otherRequests.passwordPick
                    : STR.accessCommon.passwordHandlingHint[mode]}
                </span>
              </div>
              <label>
                <span className="tx-muted" style={fieldLabelStyle}>
                  {STR.otherRequests.statusLabel}
                </span>
                <select
                  className="field"
                  value={status}
                  disabled={pending}
                  onChange={(event) => {
                    setStatus(event.target.value === 'active' ? 'active' : 'needs_access');
                  }}
                  style={textFieldStyle}
                >
                  <option value="needs_access">{STR.otherRequests.statusNeeds}</option>
                  <option value="active">{STR.otherRequests.statusActive}</option>
                </select>
              </label>
            </>
          )}
        </div>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </ModalForm>
    </Modal>
  );
}

/**
 * DECLINE AN OTHER REQUEST — the reason is OPTIONAL here (a catalog deny still needs one,
 * spec open item 1c). Given: it is in the email and the audit log. Not given: the email says
 * no reason was given.
 */
export function OtherDeclineDialog({
  request,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  request: OtherRequestView;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      title={STR.otherRequests.declineTitle(request.what)}
      lead={STR.otherRequests.declineLead(request.personName)}
      dismissable={!pending}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="ghost"
            style={modalButtonStyle}
            disabled={pending}
            onClick={onCancel}
          >
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="dbtn"
            style={modalButtonStyle}
            disabled={pending}
            onClick={() => {
              const trimmed = reason.trim();
              onConfirm(trimmed === '' ? null : trimmed);
            }}
          >
            {pending ? STR.access.queue.denying : STR.otherRequests.decline}
          </button>
        </>
      }
    >
      <ModalForm>
        <label>
          <span style={fieldLabelStyle}>{STR.otherRequests.reasonLabel}</span>
          <textarea
            className="field"
            rows={3}
            value={reason}
            placeholder={STR.otherRequests.reasonPlaceholder}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            style={{ ...textFieldStyle, lineHeight: 1.5, resize: 'vertical' }}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.otherRequests.reasonHint}
          </span>
        </label>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </ModalForm>
    </Modal>
  );
}
